// @flow strict

import type { BackgroundSizeCalculator } from '../types';

/**
 * The minimum percent of image to be shown if the aspect ratios of image and layout are out of proportion.
 * This is used when fitStrategy is set to 'calculate'. With its value set to 0.75, at least 75% of the
 * image is shown always.
 */
const FIT_STRATEGY_MIN_IMAGE_PERCENT = 0.75;

/**
 * This method returns a fitStrategy so that at least
 * FIT_STRATEGY_MIN_IMAGE_PERCENT of the image is visible in the container.
 */
const defaultBackgroundSizeCalculator: BackgroundSizeCalculator = (
  imgWidth: number,
  imgHeight: number,
  layoutWidth: number,
  layoutHeight: number,
) => {
  if (layoutHeight === 0 || layoutWidth === 0 || imgHeight === 0 || imgWidth === 0) {
    return 'cover';
  }

  const imageAspectRatio = imgWidth / imgHeight;
  const layoutAspectRatio = layoutWidth / layoutHeight;

  if (imageAspectRatio === 0 || layoutAspectRatio === 0) {
    return 'cover';
  }

  const imageTooTall = imageAspectRatio < layoutAspectRatio;
  const percentImageShownWhenUsingCover = imageTooTall
    ? imageAspectRatio / layoutAspectRatio
    : layoutAspectRatio / imageAspectRatio;

  if (percentImageShownWhenUsingCover > FIT_STRATEGY_MIN_IMAGE_PERCENT) {
    // We aren't hiding an excessive amount when using cover, so let the image fill the layout
    return 'cover';
  }

  // We are hiding too much of the image. Scale it so that at least FIT_STRATEGY_MIN_IMAGE_PERCENT is visible
  const scaleFactor = percentImageShownWhenUsingCover / FIT_STRATEGY_MIN_IMAGE_PERCENT;
  return imageTooTall
    ? `${scaleFactor * 100}% auto` // scale width setting height to auto
    : `auto ${scaleFactor * 100}%`; // scale height setting width to auto
};

export default defaultBackgroundSizeCalculator;
